import { useLocalization } from '@lib/localization';
import { has } from '@lib/permission';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { pendingDotStyles } from '@pages/AppSettings.styles';
import { countPillStyles, dangerHoverStyles, typeIconStyles } from '@pages/CustomPoiTypes.styles';
import { designRowStyles } from '@pages/ReportDesigns.styles';
import { type ReportDesignSummary } from '@store/api/reportDesignEndpoints';
import { CopyIcon, FileAltIcon, TrashIcon } from 'react-line-awesome';

interface Props {
  design: ReportDesignSummary;
  /** Whether the reader left changes to it in the editor that are not saved: they are there again when it is opened. */
  unsaved: boolean;
  /** Whether the reader may create a design anywhere, which is all a copy needs besides reading this one. */
  canCopy: boolean;
  /** False while the places a copy can go are still being read: the action waits, and does not say it is forbidden. */
  copyReady: boolean;
  /** Opens the design in the editor: read-only for a reader who may not change it. */
  onOpen: () => void;
  onCopy: () => void;
  onDelete: () => void;
}

/**
 * One design in the list: what it is called, when it last changed and what can be done to
 * it as a whole. The row itself opens the design. A design the reader left unsaved changes
 * to carries the mark the owner picker of the app settings puts on an owner.
 */
const ReportDesignRow: React.FC<Props> = ({ design, unsaved, canCopy, copyReady, onOpen, onCopy, onDelete }) => {
  const { t } = useLocalization();

  // An action the reader lacks stays in its place, disabled and saying why. The tooltip names
  // the wrapper a disabled button needs to show one at all, not the button, so the button is
  // named itself: by what it does, whatever the tooltip says.
  const action = (
    title: string, allowed: boolean, icon: React.ReactNode, onClick: () => void, { danger = false, ready = true } = {}
  ) => (
    <Tooltip title={allowed ? title : t('Permission_NotAllowed')} arrow>
      <span>
        <IconButton
          size="small"
          aria-label={title}
          onClick={onClick}
          disabled={!allowed || !ready}
          sx={danger ? dangerHoverStyles : { color: 'text.secondary' }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Stack
      direction="row"
      spacing={2}
      role="link"
      tabIndex={0}
      sx={designRowStyles}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && event.target === event.currentTarget) onOpen();
      }}
    >
      <Box sx={typeIconStyles}>
        <FileAltIcon />
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontWeight: 600 }} noWrap>{design.name}</Typography>
          {/* A dot says nothing to a screen reader, so it is named; the tooltip says the same to a pointer. */}
          {unsaved && (
            <Tooltip title={t('Customization_Designer_Unsaved')} arrow>
              <Box component="span" role="img" aria-label={t('Customization_Designer_Unsaved')} sx={pendingDotStyles} />
            </Tooltip>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" noWrap>{design.description}</Typography>
      </Box>

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0 }}>
        <Tooltip title={t('Common_DateUpdated')} arrow>
          <Box component="span" sx={countPillStyles}>{new Date(design.updatedAt).toAgoString()}</Box>
        </Tooltip>

        {/* No click on an action is a click on the row, a click on a disabled one included. */}
        <Stack direction="row" spacing={0.5} onClick={(event) => event.stopPropagation()}>
          {action(t('Customization_Duplicate'), canCopy, <CopyIcon />, onCopy, { ready: copyReady })}
          {action(t('Common_Delete'), has(design, 'report_design:delete'), <TrashIcon />, onDelete, { danger: true })}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ReportDesignRow;
